import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Smartphone, Loader2, Sparkles, RefreshCw, Megaphone, AlarmClock, Send } from "lucide-react";
import { Card, CardContent } from "./ui/card";
import { Button } from "./ui/button";
import { Switch } from "./ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { getPushSettings, updatePushSettings, sendTestPush } from "../services/api";
import { isPushSupported, getPushSubscription, enablePush, disablePush } from "../utils/push";

const isIOS = () => /iPad|iPhone|iPod/.test(navigator.userAgent);
const isStandalone = () => window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone === true;

const TYPE_ROWS = [
  {
    key: "subscription_renewal",
    icon: RefreshCw,
    color: "text-emerald-600 bg-emerald-50",
    title: "Renouvellement de forfait",
    desc: "Quand un forfait Communication se renouvelle et que la facture brouillon est générée",
  },
  {
    key: "post_time",
    icon: Megaphone,
    color: "text-violet-600 bg-violet-50",
    title: "Heure de publication",
    desc: "Quand c'est l'heure de poster une publication programmée du calendrier de diffusion",
  },
  {
    key: "post_reminder",
    icon: AlarmClock,
    color: "text-amber-600 bg-amber-50",
    title: "Rappel avant publication",
    desc: "Un rappel envoyé avant l'heure prévue de la publication",
    hasDelay: true,
  },
];

export const PushSettingsPanel = () => {
  const [supported] = useState(isPushSupported());
  const [deviceEnabled, setDeviceEnabled] = useState(false);
  const [deviceLoading, setDeviceLoading] = useState(false);
  const [settings, setSettings] = useState(null);
  const [testing, setTesting] = useState(false);

  useEffect(() => {
    getPushSettings().then((res) => setSettings(res.data)).catch(() => {});
    if (isPushSupported()) {
      getPushSubscription().then((sub) => setDeviceEnabled(!!sub)).catch(() => {});
    }
  }, []);

  const toggleDevice = async () => {
    try {
      setDeviceLoading(true);
      if (deviceEnabled) {
        await disablePush();
        setDeviceEnabled(false);
        toast.success("Notifications push désactivées sur cet appareil");
      } else {
        await enablePush();
        setDeviceEnabled(true);
        toast.success("Notifications push activées sur cet appareil 🎉");
      }
      getPushSettings().then((res) => setSettings(res.data)).catch(() => {});
    } catch (err) {
      toast.error(err.message || "Impossible d'activer les notifications");
    } finally {
      setDeviceLoading(false);
    }
  };

  const toggleType = async (key, value) => {
    try {
      const res = await updatePushSettings({ types: { [key]: value } });
      setSettings(res.data);
      toast.success(value ? "Notification activée" : "Notification désactivée");
    } catch (err) {
      toast.error("Erreur");
    }
  };

  const changeDelay = async (minutes) => {
    try {
      const res = await updatePushSettings({ reminder_minutes: parseInt(minutes) });
      setSettings(res.data);
      toast.success(`Rappel ${minutes} minutes avant la publication`);
    } catch (err) {
      toast.error("Erreur");
    }
  };

  const handleTest = async () => {
    try {
      setTesting(true);
      const res = await sendTestPush();
      if (res.data.sent > 0) {
        toast.success(`Notification test envoyée à ${res.data.sent} appareil${res.data.sent > 1 ? "s" : ""}`);
      } else {
        toast.info("Aucun appareil abonné — activez d'abord les notifications sur cet appareil");
      }
    } catch (err) {
      toast.error("Erreur lors de l'envoi du test");
    } finally {
      setTesting(false);
    }
  };

  return (
    <Card className="bg-white border-slate-200" data-testid="push-settings-panel">
      <CardContent className="p-4 lg:p-5 space-y-4">
        {/* Activation appareil */}
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-start gap-3">
            <div className="p-2 rounded-lg bg-teal-50 text-[#26788A]"><Smartphone size={20} /></div>
            <div>
              <h3 className="font-semibold text-slate-900 text-sm">Notifications push sur cet appareil</h3>
              <p className="text-xs text-slate-500 mt-0.5">
                {supported
                  ? deviceEnabled
                    ? `Activées · ${settings?.devices_count ?? 1} appareil${(settings?.devices_count ?? 1) > 1 ? "s" : ""} abonné${(settings?.devices_count ?? 1) > 1 ? "s" : ""}`
                    : "Recevez les alertes même quand l'application est fermée"
                  : "Non supporté par ce navigateur"}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {deviceLoading && <Loader2 size={14} className="animate-spin text-slate-400" />}
            <Switch
              checked={deviceEnabled}
              onCheckedChange={toggleDevice}
              disabled={!supported || deviceLoading}
              data-testid="device-push-switch"
            />
          </div>
        </div>

        {/* Aide iOS */}
        {isIOS() && !isStandalone() && (
          <div className="text-xs bg-amber-50 border border-amber-200 text-amber-800 rounded-lg px-3 py-2" data-testid="ios-install-hint">
            📱 <strong>Sur iPhone / iPad :</strong> les notifications push nécessitent d'installer l'application.
            Dans Safari, touchez <strong>Partager</strong> → <strong>« Sur l'écran d'accueil »</strong>, puis ouvrez l'app installée et activez les notifications ici.
          </div>
        )}

        {/* Types de notifications */}
        <div className="border-t border-slate-100 pt-3">
          <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Types de notifications</h4>
          <div className="space-y-3">
            {TYPE_ROWS.map((row) => {
              const Icon = row.icon;
              const enabled = settings?.types?.[row.key] ?? true;
              return (
                <div key={row.key} className="flex items-start justify-between gap-3" data-testid={`notif-type-${row.key}`}>
                  <div className="flex items-start gap-3">
                    <div className={`p-1.5 rounded-lg ${row.color}`}><Icon size={16} /></div>
                    <div>
                      <p className="text-sm font-medium text-slate-800">{row.title}</p>
                      <p className="text-xs text-slate-500">{row.desc}</p>
                      {row.hasDelay && enabled && (
                        <div className="flex items-center gap-2 mt-1.5">
                          <span className="text-xs text-slate-500">Envoyer</span>
                          <Select value={String(settings?.reminder_minutes ?? 30)} onValueChange={changeDelay}>
                            <SelectTrigger className="h-7 w-24 text-xs" data-testid="reminder-delay-select">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {[10, 15, 30, 60, 120].map((m) => (
                                <SelectItem key={m} value={String(m)}>{m} min</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <span className="text-xs text-slate-500">avant la publication</span>
                        </div>
                      )}
                    </div>
                  </div>
                  <Switch
                    checked={enabled}
                    onCheckedChange={(v) => toggleType(row.key, v)}
                    data-testid={`notif-type-switch-${row.key}`}
                  />
                </div>
              );
            })}
          </div>
        </div>

        {/* Test */}
        <div className="border-t border-slate-100 pt-3 flex justify-end">
          <Button variant="outline" size="sm" onClick={handleTest} disabled={testing} data-testid="test-push-btn">
            {testing ? <Loader2 size={14} className="animate-spin mr-1.5" /> : <Send size={14} className="mr-1.5" />}
            Envoyer une notification test
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};
